import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Radio,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { HttpCache, HttpUserClient } from "../../http";
import { EntityWithIdAndDescription } from "../../types/baseEntities";
import {
    UserClient,
    UserClientCreateUpdateDTO,
    UserClientFields,
} from "../../types/user/userClient";



import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { alpha } from "@mui/material/styles";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";
import {useUserClients} from "@/contexts/UserDataContext";



const TEXT = {
    pageTitle: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
    cardTitle: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 700,
    },
    meta: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.8,
    },
    label: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        color: "text.secondary",
    },
    value: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    helper: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.85,
    },
} as const;

const ICON_SX = {
    ...BUTTON_STYLES.icon,
    height: 32,
    width: 32,
} as const;

const ICON_DANGER_SX = {
    ...ICON_SX,
    color: "error.main",
    "&:hover": { backgroundColor: "transparent", color: "error.dark" },
} as const;

const DANGER_BUTTON_SX = {
    ...BUTTON_STYLES.invertedPrimary,
    width: "auto",
    minWidth: "fit-content",
    backgroundColor: "error.main",
    "&:before": { display: "none" },
    "&:hover": {
        backgroundColor: "error.dark",
        color: "white",
    },
} as const;

const FIELD_SX = {
    "& .MuiInputLabel-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: 0,
    },
    "& .MuiInputBase-input": {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    "& .MuiSelect-select": {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    "& .MuiFormHelperText-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        marginLeft: 0,
    },
} as const;





/* =========================
   Constantes dominio
========================= */
const DOC_DNI = 1;
const DOC_CUIT = 3;

const PERSONA_FISICA = 2;
const PERSONA_JURIDICA = 3;

/* =========================
   Helpers
========================= */
const onlyDigits = (v: string) => (v ?? "").replace(/\D/g, "");

const getTipoClientePorCuit = (cuitRaw: string): number => {
    const cuit = onlyDigits(cuitRaw);
    if (cuit.length < 2) return PERSONA_FISICA;

    const pref = parseInt(cuit.substring(0, 2), 10);
    if ([20, 23, 24, 27].includes(pref)) return PERSONA_FISICA;

    return PERSONA_JURIDICA;
};

/* =========================
   Schema
========================= */
type ClientUpsertForm = {
    codDocumentoTipo: number;
    numeroDocumento: string;
    codPersonaTipo: number;
    nombre?: string | null;
    apellido?: string | null;
    razonSocial?: string | null;
};

const schema: yup.ObjectSchema<ClientUpsertForm> = yup.object({
    codDocumentoTipo: yup
        .number()
        .required("Debe seleccionar un tipo de documento")
        .oneOf([DOC_DNI, DOC_CUIT], "Tipo de documento inválido"),

    numeroDocumento: yup
        .string()
        .required("Debe ingresar el número de documento")
        .test("digits", "Solo números", (v) => onlyDigits(v ?? "").length > 0),

    codPersonaTipo: yup.number().required(),

    nombre: yup.string().nullable().when("codPersonaTipo", {
        is: PERSONA_FISICA,
        then: (s) => s.required("El nombre es obligatorio").nullable(),
        otherwise: (s) => s.nullable(),
    }),

    apellido: yup.string().nullable().when("codPersonaTipo", {
        is: PERSONA_FISICA,
        then: (s) => s.required("El apellido es obligatorio").nullable(),
        otherwise: (s) => s.nullable(),
    }),

    razonSocial: yup.string().nullable().when("codPersonaTipo", {
        is: PERSONA_JURIDICA,
        then: (s) => s.required("La razón social es obligatoria").nullable(),
        otherwise: (s) => s.nullable(),
    }),
});

export const ProfileClientsSection = () => {
    const { clients, active, refresh } = useUserClients();

    const [docTypes, setDocTypes] = useState<EntityWithIdAndDescription[]>([]);
    const [personTypes, setPersonTypes] = useState<EntityWithIdAndDescription[]>([]);
    const [loadingCache, setLoadingCache] = useState(true);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [selectId, setSelectId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const {
        handleSubmit,
        control,
        reset,
        watch,
        setValue,
        getValues,
    } = useForm<ClientUpsertForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            codDocumentoTipo: DOC_DNI,
            numeroDocumento: "",
            codPersonaTipo: PERSONA_FISICA,
            nombre: "",
            apellido: "",
            razonSocial: "",
        },
    });

    const codDocumentoTipo = watch("codDocumentoTipo");
    const numeroDocumento = watch("numeroDocumento");
    const codPersonaTipo = watch("codPersonaTipo");

    /* =========================
       Cache
    ========================= */
    useEffect(() => {
        const load = async () => {
            setLoadingCache(true);
            try {
                const [docs, persons] = await Promise.all([
                    HttpCache.getIdentityDocumentTypes(),
                    HttpCache.getPersonTypes(),
                ]);

                setDocTypes((docs ?? []).filter((d) => d.id !== 2));
                setPersonTypes(persons ?? []);
            } finally {
                setLoadingCache(false);
            }
        };

        load();
    }, []);

    useEffect(() => {
        if (codDocumentoTipo === DOC_DNI) {
            if (codPersonaTipo !== PERSONA_FISICA) {
                setValue("codPersonaTipo", PERSONA_FISICA);
            }
        }

        if (codDocumentoTipo === DOC_CUIT) {
            const tipo = getTipoClientePorCuit(numeroDocumento);
            if (tipo !== codPersonaTipo) {
                setValue("codPersonaTipo", tipo);
            }
        }

        if (codPersonaTipo === PERSONA_JURIDICA) {
            const currentNombre = getValues("nombre");
            const currentApellido = getValues("apellido");

            if (currentNombre !== null) setValue("nombre", null);
            if (currentApellido !== null) setValue("apellido", null);
        }
    }, [codDocumentoTipo, numeroDocumento, codPersonaTipo, setValue, getValues]);

    /* =========================
       Labels
    ========================= */
    const personaTipoDesc = useMemo(() => {
        const p = personTypes.find((x) => x.id === codPersonaTipo);
        return p?.descripcion ?? "";
    }, [codPersonaTipo, personTypes]);

    const docTipoDesc = useMemo(() => {
        const d = docTypes.find((x) => x.id === codDocumentoTipo);
        return d?.descripcion ?? "";
    }, [codDocumentoTipo, docTypes]);

    /* =========================
       Helpers UI flow
    ========================= */
    const startCreating = () => {
        setError("");
        reset({
            codDocumentoTipo: DOC_DNI,
            numeroDocumento: "",
            codPersonaTipo: PERSONA_FISICA,
            nombre: "",
            apellido: "",
            razonSocial: "",
        });
        setIsCreating(true);
        setEditingId(null);
    };

    const startEditing = (client: UserClient) => {
        setError("");

        reset({
            codDocumentoTipo: client[UserClientFields.IdentityDocumentTypeId],
            numeroDocumento: client[UserClientFields.IdentityDocumentNumber],
            codPersonaTipo: client[UserClientFields.PersonTypeId],
            nombre: client[UserClientFields.Name] ?? "",
            apellido: client[UserClientFields.Surname] ?? "",
            razonSocial: client[UserClientFields.CompanyName] ?? "",
        });

        setEditingId(client.id);
        setIsCreating(false);
    };

    const buildDto = (data: ClientUpsertForm): UserClientCreateUpdateDTO => {
        const docNumber = onlyDigits(data.numeroDocumento);

        let personaTipo = data.codPersonaTipo;

        if (data.codDocumentoTipo === DOC_DNI) {
            personaTipo = PERSONA_FISICA;
        }

        if (data.codDocumentoTipo === DOC_CUIT) {
            personaTipo = getTipoClientePorCuit(docNumber);
        }

        let nombre = data.nombre ?? null;
        let apellido = data.apellido ?? null;

        let razonSocial: string;
        if (personaTipo === PERSONA_FISICA) {
            razonSocial = `${apellido ?? ""}-${nombre ?? ""}`.trim();
        } else {
            razonSocial = (data.razonSocial ?? "").trim();
            nombre = null;
            apellido = null;
        }

        return {
            [UserClientFields.PersonTypeId]: personaTipo,
            [UserClientFields.IdentityDocumentTypeId]: data.codDocumentoTipo,
            [UserClientFields.IdentityDocumentNumber]: docNumber,
            [UserClientFields.Name]: personaTipo === PERSONA_FISICA ? nombre : null,
            [UserClientFields.Surname]: personaTipo === PERSONA_FISICA ? apellido : null,
            [UserClientFields.CompanyName]: razonSocial,
        };
    };

    /* =========================
       Submit create/edit
    ========================= */
    const submitCreate = async (data: ClientUpsertForm) => {
        setSaving(true);
        setError("");
        try {
            const dto = buildDto(data);
            const resp = await HttpUserClient.createClient(dto);

            if ((resp as any).tieneError) {
                setError((resp as any).descripcionError);
                return;
            }

            const createdId =
                (resp as any).data ?? (resp as any).valor ?? (resp as any).id ?? null;

            setIsCreating(false);
            await refresh();

        } catch (e: any) {
            setError(e?.message ?? "Error al crear cliente");
        } finally {
            setSaving(false);
        }
    };

    const submitEdit = async (data: ClientUpsertForm) => {
        if (!editingId) return;

        setSaving(true);
        setError("");
        try {
            const dto = buildDto(data);
            const resp = await HttpUserClient.updateClient(editingId, dto);

            if ((resp as any).tieneError) {
                setError((resp as any).descripcionError);
                return;
            }

            setEditingId(null);
            await refresh();
        } catch (e: any) {
            setError(e?.message ?? "Error al actualizar cliente");
        } finally {
            setSaving(false);
        }
    };

    /* =========================
       Delete
    ========================= */
    const deleteClient = async () => {
        if (!deleteId) return;

        setSaving(true);
        setError("");
        try {
            const resp = await HttpUserClient.deleteClient(deleteId);

            if ((resp as any).tieneError) {
                setError((resp as any).descripcionError);
                return;
            }

            setDeleteId(null);
            await refresh();
        } catch (e: any) {
            setError(e?.message ?? "Error al eliminar cliente");
        } finally {
            setSaving(false);
        }
    };

    const confirmSelection = async () => {
        if (!selectId) return;

        try {
            const resp = await HttpUserClient.selectClient(selectId);
            if ((resp as any).tieneError) {
                alert((resp as any).descripcionError);
                return;
            }

            await refresh();
            setSelectId(null);
        } catch (e: any) {
            alert(e?.message ?? "Error al seleccionar cliente");
        }
    };

    const renderClientForm = (mode: "create" | "edit") => (
        <Box component="form" onSubmit={handleSubmit(mode === "create" ? submitCreate : submitEdit)}>
            <Controller
                name="codDocumentoTipo"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        select
                        label="Tipo de documento"
                        fullWidth
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ ...FIELD_SX, mb: 2 }}
                    >
                        <MenuItem value={DOC_DNI}>DNI</MenuItem>
                        <MenuItem value={DOC_CUIT}>CUIT</MenuItem>
                    </TextField>
                )}
            />

            <Controller
                name="numeroDocumento"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        label={`Número ${docTipoDesc ? `(${docTipoDesc})` : ""}`}
                        fullWidth
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ ...FIELD_SX, mb: 2 }}
                    />
                )}
            />


            {codPersonaTipo === PERSONA_FISICA && (
                <>
                    <Controller
                        name="nombre"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="Nombre"
                                fullWidth
                                {...field}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                sx={{ ...FIELD_SX, mb: 2 }}
                            />
                        )}
                    />

                    <Controller
                        name="apellido"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="Apellido"
                                fullWidth
                                {...field}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                sx={{ ...FIELD_SX, mb: 2 }}
                            />
                        )}
                    />


                </>
            )}

            {codPersonaTipo === PERSONA_JURIDICA && (
                <Controller
                    name="razonSocial"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            label="Razón social"
                            fullWidth
                            {...field}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={{ mb: 2 }}
                        />
                    )}
                />
            )}

            <Box display="flex" gap={2}>
                <Button
                    variant="contained"
                    type="submit"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} /> : undefined}
                    sx={{
                        ...BUTTON_STYLES.invertedPrimary,
                        width: "auto",
                        minWidth: "fit-content",
                    }}
                >
                    Guardar
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => {
                        setError("");
                        if (mode === "create") setIsCreating(false);
                        else setEditingId(null);
                    }}
                    disabled={saving}
                    sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                >
                    Cancelar
                </Button>
            </Box>


            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
    );

    /* =========================
       Main render
    ========================= */
    if (loadingCache) return <Typography sx={TEXT.value}>Cargando...</Typography>;

    return (
        <Box>
            <Typography sx={{ ...TEXT.pageTitle, mb: 3 }}>Clientes</Typography>


            {clients.length === 0 ? (
                <Typography sx={TEXT.value}>No tenés clientes cargados.</Typography>
            ) : (
                clients.map((c) => (
                    <Paper
                        key={c.id}
                        onClick={() => {
                            if (editingId === null && !isCreating) setSelectId(c.id);
                        }}
                        sx={() => ({
                            p: 2,
                            mb: 2,
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,

                            border: "1px solid",
                            borderColor: c.seleccionado ? STYLES.colors.secondary : "#ddd",

                            backgroundColor: c.seleccionado
                                ? alpha(STYLES.colors.secondary, 0.06)
                                : "transparent",

                            cursor: editingId ? "default" : "pointer",
                            transition: "0.2s",

                            "&:hover": {
                                borderColor: editingId ? undefined : STYLES.colors.secondary,
                                backgroundColor: editingId ? undefined : alpha(STYLES.colors.secondary, 0.05),
                            },

                            "&::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: 5,
                                height: "100%",
                                backgroundColor: STYLES.colors.secondary,
                                opacity: c.seleccionado ? 1 : 0,
                                transition: "opacity 0.2s ease",
                            },
                        })}

                    >
                        {editingId === c.id ? (
                            renderClientForm("edit")
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                    <Radio
                                        checked={!!c.seleccionado}
                                        size="small"
                                        disableRipple
                                        sx={{
                                            mt: 0.25,
                                            p: 0,
                                            color: alpha(STYLES.colors.secondary, 0.45),
                                            "&.Mui-checked": {
                                                color: STYLES.colors.secondary,
                                            },
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 18,
                                            },
                                        }}
                                    />


                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={TEXT.cardTitle}>
                                        {c[UserClientFields.CompanyName]}
                                    </Typography>

                                    <Typography sx={TEXT.meta}>
                                        {c[UserClientFields.PersonTypeId] === PERSONA_FISICA &&
                                            (c[UserClientFields.Name] || c[UserClientFields.Surname]) ? (
                                            <>
                                                {[c[UserClientFields.Name], c[UserClientFields.Surname]]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                                {" · "}
                                            </>
                                        ) : null}

                                        {c[UserClientFields.IdentityDocumentTypeDesc]} {c[UserClientFields.IdentityDocumentNumber]}
                                        {" · "}
                                        Persona {c[UserClientFields.PersonTypeDesc]}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, alignSelf: "center" }}>
                                    <IconButton
                                        sx={ICON_SX}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startEditing(c);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>

                                    <IconButton
                                        sx={ICON_DANGER_SX}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteId(c.id);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Paper>

                ))
            )}

            {!isCreating ? (
                <Paper
                    onClick={() => startCreating()}
                    sx={{
                        p: 3,
                        mt: 3,
                        cursor: "pointer",
                        border: "1px dashed #999",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        transition: "0.2s",
                        "&:hover": {
                            borderColor: "#000",
                            backgroundColor: "#fafafa",
                        },
                    }}
                >
                    <Typography sx={{ ...TEXT.value, fontWeight: 700, fontSize: "15px" }}>
                        + Agregar nuevo cliente
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ p: 2, mb: 2, mt: 3, border: "1px dashed #999", borderRadius: 2 }}>
                    {renderClientForm("create")}
                </Paper>
            )}

            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>
                    <Typography sx={TEXT.cardTitle}>Eliminar cliente</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={TEXT.value}>
                        ¿Estás seguro que querés eliminar el cliente?
                        <br />
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 2, px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteId(null)}
                        sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={deleteClient}
                        disabled={saving}
                        sx={DANGER_BUTTON_SX}
                    >
                        Eliminar
                    </Button>
                </DialogActions>

            </Dialog>

            <Dialog open={!!selectId} onClose={() => setSelectId(null)}>
                <DialogTitle>
                    <Typography sx={TEXT.cardTitle}>Cambiar cliente activo</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={TEXT.value}>
                        Vas a cambiar tu cliente activo.
                        <br />
                        Esto puede afectar la facturación y los datos del checkout.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 2, px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setSelectId(null)}
                        sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={confirmSelection}
                        sx={{ ...BUTTON_STYLES.primary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cambiar cliente
                    </Button>
                </DialogActions>


            </Dialog>
        </Box>
    );
};














